/**
 * ChatMessages — Message rendering for Ask Argus
 *
 * Demo-port design (R2): lean, scroll-to-bottom thread. No virtualizer,
 * no jump-to-bottom FAB, no roving tabindex. The demo prioritizes a
 * clean reading experience; advanced affordances can come back as
 * separate components when the founder asks for them.
 *
 * USES PRODUCTION COMPONENTS — does NOT recreate them.
 *
 * Wired to real `Message` objects emitted by useChatStream:
 *   - `message.content`         from `token` SSE events
 *   - `message.citations`       from `citation` SSE events
 *   - `message.toolCalls`       from `tool_call` SSE events
 *   - `message.confidenceScore` from `confidence` SSE events
 *   - `message.pipeline`        from `pipeline` SSE events (real trace)
 *   - `message.error`           from `error` SSE events
 *
 * Text-selection actions (Ask/Explain/Copy/Quote) come from
 * `GlobalSelectionToolbar` mounted in `__root.tsx`. The demo's local
 * SelectionToolbar is intentionally NOT mounted here — that double-popup
 * regression was fixed in PR-AAU-D2 and we keep the production wiring.
 */

import { useRef, useEffect, useState } from 'react';
import { User, AlertTriangle, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ArgusEye } from '@/components/icons';
import {
  MarkdownRenderer,
  MessageActions,
  ToolCallCard,
  ThinkingBlock,
  parseThinkingBlocks,
  ResponseTraceDrawer,
  CitationModal,
} from '@/components/chat';
import { ConfidenceChip } from './confidence-chip';
import { ChatErrorCard } from './chat-error-card';
import type { Message } from '@/lib/chat-types';
import { toRendererCitations } from '@/lib/chat/message-hydration';
import {
  useCitationVisibility,
  stripCitationMarkers,
} from '@/contexts/citation-visibility-context';

// ── Message List ────────────────────────────────────────────────────────────

/**
 * Streaming phase for the Ask Argus thinking/responding indicator.
 *
 * Maps to ArgusEye `state` values so the indicator uses the product
 * visual language instead of a generic `Loader2` spinner (issue #1323).
 *
 *   - `waiting`    → ArgusEye state="listening" (cyan)
 *   - `thinking`   → ArgusEye state="thinking" (blue)
 *   - `tool_use`   → ArgusEye state="listening" (cyan, attentive)
 *   - `responding` → ArgusEye state="speaking" (purple, output flowing)
 *   - `error`      → ArgusEye state="alert" (amber)
 *
 * Callers that already track stream phase (via `useChatStream`'s
 * snapshot.phase) can pass it through. Callers that don't pass anything
 * default to `thinking`, which is the behaviour the user sees today for
 * a typical request.
 */
export type ChatStreamingPhase =
  | 'waiting'
  | 'thinking'
  | 'tool_use'
  | 'responding'
  | 'error';

interface ChatMessagesProps {
  messages: Message[];
  /** True while an assistant turn is still streaming. */
  isStreaming?: boolean;
  /**
   * Optional fine-grained phase. When omitted, the indicator falls back
   * to `thinking`. See `ChatStreamingPhase` for the mapping.
   */
  streamingPhase?: ChatStreamingPhase;
  /** Optional backend stage label shown while no tokens have arrived yet. */
  streamingLabel?: string;
  /**
   * Re-send the prompt that produced a failed assistant turn. Wired to
   * the ChatErrorCard's "Retry" button.
   */
  onRetry?: (prompt: string) => void;
  /**
   * Thumbs-up / thumbs-down rating. Persisted by the parent via
   * `POST /conversations/:id/messages/:msgId/feedback`.
   */
  onRate?: (messageId: string, rating: 'positive' | 'negative', reason?: string) => void;
  /** Re-roll the assistant response for a specific message. */
  onRegenerate?: (messageId: string) => void;
  /** "Continue" action on the last assistant bubble. */
  onContinue?: (messageId: string) => void;
  /** Edit + re-send a user message. */
  onEdit?: (messageId: string, newContent: string) => void;
  /** bug #23 — send a proactive follow-up suggestion as a new turn. */
  onFollowUp?: (text: string) => void;
}

/**
 * Maps a live `useChatStream` phase → ChatStreamingPhase.
 *
 * The chat-stream controller exposes its own phase machine (`'idle' |
 * 'awaiting' | 'tool_calling' | 'streaming' | 'error' | 'done'`) tied
 * to SSE event types. ChatMessages' visual indicator takes a slightly
 * different vocabulary (`'waiting' | 'thinking' | 'tool_use' |
 * 'responding' | 'error'`) so the mapping is explicit and tested.
 *
 * Mapping (locked by Codex review on PR #1328, issue #1323):
 *
 *   awaiting     → waiting
 *   tool_calling → tool_use
 *   streaming    → responding
 *   error        → error
 *   idle / done  → undefined (caller renders no indicator when isStreaming=false;
 *                   if forced via isStreaming=true, ArgusEye falls back to
 *                   thinking via the secondary mapper)
 *
 * Exported for tests.
 */
export function chatStreamPhaseToMessagesPhase(
  phase:
    | 'idle'
    | 'awaiting'
    | 'thinking'
    | 'tool_calling'
    | 'streaming'
    | 'error'
    | 'done'
    | undefined,
): ChatStreamingPhase | undefined {
  switch (phase) {
    case 'awaiting':
      return 'waiting';
    case 'thinking':
      return 'thinking';
    case 'tool_calling':
      return 'tool_use';
    case 'streaming':
      return 'responding';
    case 'error':
      return 'error';
    case 'idle':
    case 'done':
    case undefined:
    default:
      return undefined;
  }
}

/**
 * Maps ChatStreamingPhase → ArgusEye state. Exported for tests so the
 * mapping can be locked down as a contract; the component re-uses it
 * inline below.
 */
export function streamingPhaseToArgusState(
  phase: ChatStreamingPhase | undefined,
): 'thinking' | 'listening' | 'speaking' | 'alert' {
  switch (phase) {
    case 'waiting':
      return 'listening';
    case 'tool_use':
      return 'listening';
    case 'responding':
      return 'speaking';
    case 'error':
      return 'alert';
    case 'thinking':
    case undefined:
    default:
      return 'thinking';
  }
}

/**
 * Maps ChatStreamingPhase → indicator label. Exported for tests.
 */
export function streamingPhaseToLabel(
  phase: ChatStreamingPhase | undefined,
): string {
  switch (phase) {
    case 'waiting':
      return 'Waiting…';
    case 'tool_use':
      return 'Using tools…';
    case 'responding':
      return 'Responding…';
    case 'error':
      return 'Recovering…';
    case 'thinking':
    case undefined:
    default:
      return 'Thinking…';
  }
}

export function ChatMessages({
  messages,
  isStreaming,
  streamingPhase,
  streamingLabel,
  onRetry,
  onRate,
  onRegenerate,
  onContinue,
  onEdit,
  onFollowUp,
}: ChatMessagesProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [traceMessage, setTraceMessage] = useState<Message | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Find the index of the LAST assistant message — used to gate the
  // "Continue" action so it only appears on the final answer bubble.
  let lastAssistantIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') {
      lastAssistantIdx = i;
      break;
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative flex-1 overflow-y-auto px-4 py-6"
      data-testid="ask-argus-message-list"
    >
      <div className="max-w-3xl mx-auto space-y-6">
        {messages.map((msg, idx) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            isFinalAssistant={idx === lastAssistantIdx}
            onOpenTrace={setTraceMessage}
            onRetry={onRetry}
            onRate={onRate}
            onRegenerate={onRegenerate}
            onContinue={onContinue}
            onEdit={onEdit}
            onFollowUp={onFollowUp}
          />
        ))}

        {isStreaming && (
          <div
            className="flex items-center gap-2 text-muted-foreground text-sm pl-12"
            data-testid="ask-argus-streaming-indicator"
            data-streaming-phase={streamingPhase ?? 'thinking'}
          >
            <ArgusEye
              state={streamingPhaseToArgusState(streamingPhase)}
              blink="fast"
              className="h-5 w-5"
            />
            <span>{streamingLabel ?? streamingPhaseToLabel(streamingPhase)}</span>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Pipeline Trace Drawer — uses real pipeline stages from SSE.
          Our Message.pipeline status is `success | pending | failed | skipped`,
          but the trace drawer's PipelineStage expects `success | error | skipped`.
          Map `pending` → `skipped` (in-flight stages render as no-op) and
          `failed` → `error`. */}
      <ResponseTraceDrawer
        open={!!traceMessage}
        onOpenChange={(open) => {
          if (!open) setTraceMessage(null);
        }}
        stages={(traceMessage?.pipeline ?? []).map((s) => ({
          name: s.name,
          durationMs: s.durationMs,
          status:
            s.status === 'failed'
              ? ('error' as const)
              : s.status === 'pending'
                ? ('skipped' as const)
                : (s.status as 'success' | 'skipped'),
        }))}
        totalLatencyMs={traceMessage?.metadata?.latencyMs ?? 0}
        model={traceMessage?.metadata?.model}
        intent={traceMessage?.metadata?.intent}
        ragSourceCount={traceMessage?.metadata?.ragSourceCount}
        confidenceScore={
          traceMessage?.confidenceScore != null
            ? Math.round(traceMessage.confidenceScore * 100)
            : undefined
        }
      />
    </div>
  );
}

// ── Single Message ──────────────────────────────────────────────────────────

function MessageBubble({
  message,
  isFinalAssistant,
  onOpenTrace,
  onRetry,
  onRate,
  onRegenerate,
  onContinue,
  onEdit,
  onFollowUp,
}: {
  message: Message;
  isFinalAssistant: boolean;
  onOpenTrace?: (msg: Message) => void;
  onRetry?: (prompt: string) => void;
  onRate?: (messageId: string, rating: 'positive' | 'negative', reason?: string) => void;
  onRegenerate?: (messageId: string) => void;
  onContinue?: (messageId: string) => void;
  onEdit?: (messageId: string, newContent: string) => void;
  onFollowUp?: (text: string) => void;
}) {
  const isUser = message.role === 'user';
  const { citationsVisible } = useCitationVisibility();
  // Inline-badge click opens the source in the CitationModal (the intended
  // wiring per MarkdownRenderer's comment — previously the handler was never
  // passed, so badges were inert). Works for every tier: library docs fetch
  // full-text, MCP/web fall back to the cited text.
  const [modalCitationIdx, setModalCitationIdx] = useState<number | null>(null);

  const { thinking, visible } = isUser
    ? { thinking: [], visible: message.content }
    : parseThinkingContent(message);

  // Optionally strip [n] citation markers per the session toggle.
  const renderedContent = citationsVisible ? visible : stripCitationMarkers(visible);

  // Terminal error — render the error card in place of normal content.
  if (message.error && !isUser) {
    return (
      <div className="group relative" data-role={message.role}>
        <div className="flex gap-3">
          <div className="w-8 h-8 shrink-0 mt-0.5">
            <ArgusEye state="alert" className="w-8 h-8" />
          </div>
          <div className="flex-1 min-w-0">
            <ChatErrorCard
              error={message.error}
              retryPrompt={message.retryPrompt}
              onRetry={
                message.retryPrompt && onRetry
                  ? (prompt) => onRetry(prompt)
                  : undefined
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative" data-role={message.role}>
      <div className="flex gap-3">
        {isUser ? (
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
            <User className="h-4 w-4 text-primary" />
          </div>
        ) : (
          <div className="w-8 h-8 shrink-0 mt-0.5">
            <ArgusEye state="idle" className="w-8 h-8" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium">
              {isUser
                ? 'You'
                : message.agentDisplayName ?? 'Argus'}
            </span>
            {message.metadata?.model && !isUser && (
              <Badge variant="outline" className="text-[9px] h-4 px-1 font-normal">
                {message.metadata.model}
              </Badge>
            )}
            <span className="text-[11px] text-muted-foreground">
              {formatTime(message.timestamp)}
            </span>
          </div>

          {thinking.length > 0 && thinking.map((block, i) => (
            <ThinkingBlock key={i} content={block} />
          ))}

          {message.toolCalls && message.toolCalls.length > 0 && (
            <div className="space-y-2 mb-3">
              {message.toolCalls.map((tc, i) => (
                <ToolCallCard
                  key={i}
                  toolName={tc.toolName}
                  args={tc.args}
                  result={tc.result}
                  durationMs={tc.durationMs}
                  status={tc.status}
                  policyAction={tc.policyAction}
                />
              ))}
            </div>
          )}

          {/* bug #13 — answer-quality / ungrounded warning banner. Rendered
              when the backend gate flagged the answer as not independently
              verified (or contradicted). */}
          {!isUser && message.qualityBanner?.message && (
            <div
              data-testid="ungrounded-warning"
              className={cn(
                'mb-2 flex items-start gap-2 rounded-md border px-3 py-2 text-xs',
                message.qualityBanner.level === 'warning'
                  ? 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300'
                  : 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300',
              )}
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{message.qualityBanner.message}</span>
            </div>
          )}

          <div className="max-w-full">
            <MarkdownRenderer
              content={renderedContent}
              showCitationBadges={citationsVisible}
              onCitationClick={(idx) => setModalCitationIdx(idx)}
              citations={toRendererCitations(message.citations)}
            />
          </div>

          {/* Inline-badge click → source detail. Mounted per-bubble; closed
              state renders nothing heavy (CitationModal early-returns). */}
          {modalCitationIdx != null && message.citations?.[modalCitationIdx] && (
            <CitationModal
              citations={[
                {
                  docName: message.citations[modalCitationIdx].docName,
                  page: message.citations[modalCitationIdx].page,
                  section: message.citations[modalCitationIdx].section,
                  text: message.citations[modalCitationIdx].text,
                  score: message.citations[modalCitationIdx].score,
                  documentId: message.citations[modalCitationIdx].documentId,
                },
              ]}
              open={modalCitationIdx != null}
              onOpenChange={(o) => {
                if (!o) setModalCitationIdx(null);
              }}
            />
          )}

          {!isUser && (
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {(message.confidenceScore != null || message.confidenceRationale) && (
                <ConfidenceChip
                  confidence={message.confidenceScore}
                  rationale={message.confidenceRationale}
                  onClick={() => onOpenTrace?.(message)}
                />
              )}
              <TooltipProvider>
                <MessageActions
                  role="assistant"
                  content={message.content}
                  messageId={message.id}
                  feedback={message.feedback}
                  metadata={
                    message.metadata
                      ? {
                          model: message.metadata.model,
                          totalTokens: message.metadata.totalTokens,
                          latencyMs: message.metadata.latencyMs,
                          ragSourceCount: message.metadata.ragSourceCount,
                          confidenceScore:
                            message.confidenceScore != null
                              ? Math.round(message.confidenceScore * 100)
                              : undefined,
                        }
                      : undefined
                  }
                  onRate={
                    onRate ? (rating, reason) => onRate(message.id, rating, reason) : undefined
                  }
                  onRegenerate={onRegenerate ? () => onRegenerate(message.id) : undefined}
                  onContinue={
                    isFinalAssistant && onContinue
                      ? () => onContinue(message.id)
                      : undefined
                  }
                />
              </TooltipProvider>
              {message.metadata && (
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  {message.metadata.totalTokens && (
                    <span>{message.metadata.totalTokens} tokens</span>
                  )}
                  {message.metadata.latencyMs && (
                    <span>{(message.metadata.latencyMs / 1000).toFixed(1)}s</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* bug #23 — proactive follow-up suggestions. Clicking sends the
              suggestion as a new turn. */}
          {!isUser && message.followUps && message.followUps.length > 0 && (
            <div
              className="mt-3 flex flex-wrap gap-2"
              data-testid="follow-up-suggestions"
            >
              {message.followUps.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onFollowUp?.(s)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs text-foreground/80 transition-colors hover:bg-primary/10 hover:text-foreground"
                >
                  <Sparkles className="h-3 w-3 text-primary/70" />
                  {s}
                </button>
              ))}
            </div>
          )}

          {isUser && onEdit && (
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <TooltipProvider>
                <MessageActions
                  role="user"
                  content={message.content}
                  messageId={message.id}
                  onEdit={(newContent) => onEdit(message.id, newContent)}
                />
              </TooltipProvider>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function parseThinkingContent(message: Message): { thinking: string[]; visible: string } {
  if (message.thinkingContent) {
    return { thinking: [message.thinkingContent], visible: message.content };
  }
  try {
    const blocks = parseThinkingBlocks(message.content);
    if (blocks.length > 0) {
      const thinkingTexts = blocks.filter((b) => b.type === 'thinking').map((b) => b.content);
      const visibleTexts = blocks
        .filter((b) => b.type === 'text')
        .map((b) => b.content)
        .join('');
      if (thinkingTexts.length > 0) {
        return { thinking: thinkingTexts, visible: visibleTexts };
      }
    }
  } catch {
    /* fallback */
  }
  return { thinking: [], visible: message.content };
}
